

/**
 * Safely parse a numeric value from a string, handling spaces and dashes
 * @param {string} value - The value to parse
 * @param {number} defaultValue - Default value if parsing fails
 * @returns {number} - The parsed number or default value
 */
export function safeParseFloat(value, defaultValue = 0) {
  if (!value) {
    return defaultValue;
  }
  
  const cleanedValue = String(value).replace(/\s+/g, '');
  if (cleanedValue === '' || cleanedValue === '-') {
    return defaultValue;
  }
  
  const parsed = parseFloat(cleanedValue);
  return isNaN(parsed) ? defaultValue : parsed;
}

/**
 * Extract country from a region|grid string
 * @param {string} regionGrid - The region|grid string
 * @returns {string} - The extracted country
 */
export function extractCountry(regionGrid) {
  if (!regionGrid) {
    return '';
  }
  
  if (regionGrid.includes('|')) {
    return regionGrid.split('|')[0].trim();
  }
  
  return regionGrid;
}

/**
 * Define renewable technologies for filtering
 * @returns {Array} - Array of renewable technology strings
 */
export function getRenewableTechnologies() {
  return ['Biogas', 'Biomass', 'Geothermal', 'Hydroelectric', 'Solar', 'Wind'];
}

/**
 * Define non-renewable technology categories
 * @returns {Array} - Array of non-renewable technology category strings
 */
export function getNonRenewableTechnologies() {
  return ['coal', 'natural gas', 'nuclear', 'oil', 'diesel'];
}

/**
 * Check if a technology is renewable based on predefined categories
 * @param {string} technology - The technology string to check
 * @returns {boolean} - True if renewable, false otherwise
 */
export function isRenewableTechnology(technology) {
  if (!technology) {
    return false;
  }
  
  const renewableTechnologies = getRenewableTechnologies();
  return renewableTechnologies.some(tech => 
    technology.toLowerCase().includes(tech.toLowerCase())
  );
}

/**
 * Check if a technology is non-renewable based on predefined categories
 * @param {string} technology - The technology string to check
 * @returns {boolean} - True if non-renewable, false otherwise
 */
export function isNonRenewableTechnology(technology) {
  if (!technology) {
    return false;
  }
  
  let category = technology;
  if (technology.includes('|')) {
    category = technology.split('|')[0].trim().toLowerCase();
  } else {
    category = technology.toLowerCase();
  }
  
  const nonRenewableTechnologies = getNonRenewableTechnologies();
  return nonRenewableTechnologies.some(techCategory => 
    category.includes(techCategory) || techCategory.includes(category)
  );
}

/**
 * Create a lookup map for technology intensities
 * @param {Array} techIntensitiesData - Technology intensity data array
 * @returns {Object} - Lookup map for technology intensities
 */
const WTT_POWER_CI_FIELD = "WtT Power CI (gCO2e/kWh)";

export function createTechIntensityMap(techIntensitiesData) {
  const techIntensityMap = {};
  
  if (!Array.isArray(techIntensitiesData)) {
    return techIntensityMap;
  }
  
  // Define constant for intensity field names
  const WTT_POWER_CI_ALT_FIELD = "WtT_Power_CI";
  
  techIntensitiesData.forEach(item => {
    if (!item.Category || !item.Technology) {
      return;
    }
    
    // Store with lowercase keys for case-insensitive matching
    const key = `${item.Category.toLowerCase()} | ${item.Technology.toLowerCase()}`;
    const intensityValue = item[WTT_POWER_CI_FIELD] || item[WTT_POWER_CI_ALT_FIELD];
    techIntensityMap[key] = safeParseFloat(intensityValue);
    
    // Also store the individual components for easier partial matching
    techIntensityMap[item.Category.toLowerCase()] = safeParseFloat(intensityValue);
    techIntensityMap[item.Technology.toLowerCase()] = safeParseFloat(intensityValue);
  });
  
  return techIntensityMap;
}

/**
 * Find CI (Carbon Intensity) for a technology
 * @param {string} technology - Technology string
 * @param {Object} techIntensityMap - Technology intensity map
 * @param {Array} techIntensitiesData - Technology intensities data
 * @returns {number|null} - Carbon intensity or null if not found
 */
export function findCIForTechnology(technology, techIntensityMap, techIntensitiesData) {
  if (!technology || !techIntensityMap) {
    return null;
  }
  
  // Try to match technology exactly (case-insensitive)
  const lookupKey = technology.toLowerCase();
  if (techIntensityMap[lookupKey]) {
    return techIntensityMap[lookupKey];
  }
  
  return findCIByTechnologyParts(technology, techIntensityMap, techIntensitiesData);
}

/**
 * Find CI by breaking down technology string into parts
 * @param {string} technology - Technology string
 * @param {Object} techIntensityMap - Technology intensity map
 * @param {Array} techIntensitiesData - Technology intensities data
 * @returns {number|null} - Carbon intensity or null if not found
 */
export function findCIByTechnologyParts(technology, techIntensityMap, techIntensitiesData) {
  // Split technology string to get components
  const techParts = technology.split('|').map(part => part.trim());
  
  if (techParts.length === 2) {
    return findCIByTechnologyCategory(techParts[0], techParts[1], techIntensityMap, techIntensitiesData);
  } else {
    return findCIByRenewableCategory(technology, techIntensitiesData);
  }
}

/**
 * Find CI based on technology category and specific technology
 * @param {string} category - Technology category 
 * @param {string} tech - Specific technology
 * @param {Object} techIntensityMap - Technology intensity map
 * @param {Array} techIntensitiesData - Technology intensities data
 * @returns {number|null} - Carbon intensity or null if not found
 */
export function findCIByTechnologyCategory(category, tech, techIntensityMap, techIntensitiesData) {
  const categoryLower = category.toLowerCase();
  const techLower = tech.toLowerCase();
  
  // Look for exact match in tech intensity map
  const combinedKey = `${categoryLower} | ${techLower}`;
  if (techIntensityMap[combinedKey]) {
    return techIntensityMap[combinedKey];
  }
  
  if (!Array.isArray(techIntensitiesData)) {
    return null;
  }
  
  // Find match in tech intensity data with case-insensitive comparison
  const match = techIntensitiesData.find(t => 
    t.Category?.toLowerCase() === categoryLower && 
    t.Technology?.toLowerCase() === techLower
  );
  
  if (match) {
    const intensityValue = match[WTT_POWER_CI_FIELD] || match.WtT_Power_CI;
    return safeParseFloat(intensityValue);
  }
  
  // Try to find default for this category
  const defaultMatch = techIntensitiesData.find(t => 
    t.Category?.toLowerCase() === categoryLower && 
    t.Technology?.toLowerCase() === "default"
  );
  
  if (defaultMatch) {
    const intensityValue = defaultMatch[WTT_POWER_CI_FIELD] || defaultMatch.WtT_Power_CI;
    return safeParseFloat(intensityValue);
  }
  
  // Special case for Geothermal | Flash Power Plant
  const GEOTHERMAL = "geothermal";
  const FLASH_POWER_PLANT = "flash power plant";
  
  if (categoryLower === GEOTHERMAL && techLower === FLASH_POWER_PLANT) {
    const geothermalMatch = techIntensitiesData.find(t => 
      t.Category?.toLowerCase() === GEOTHERMAL && 
      t.Technology?.toLowerCase() === FLASH_POWER_PLANT
    );
    
    if (geothermalMatch) {
      const intensityValue = geothermalMatch[WTT_POWER_CI_FIELD] || geothermalMatch.WtT_Power_CI;
      return safeParseFloat(intensityValue);
    }
  }
  
  return null;
}

/**
 * Find CI based on matching with renewable categories
 * @param {string} technology - Technology string
 * @param {Array} techIntensitiesData - Technology intensities data
 * @returns {number|null} - Carbon intensity or null if not found
 */
export function findCIByRenewableCategory(technology, techIntensitiesData) {
  if (!Array.isArray(techIntensitiesData)) {
    return null;
  }
  
  // For single word technologies, find a match from renewables
  const renewableTechnologies = getRenewableTechnologies();
  const matched = renewableTechnologies.find(tech => 
    technology.toLowerCase().includes(tech.toLowerCase())
  );
  
  if (matched) {
    const matchingTech = techIntensitiesData.find(tech => 
      tech.Category?.toLowerCase() === matched.toLowerCase()
    );
    
    if (matchingTech) {
      const intensityField = matchingTech[WTT_POWER_CI_FIELD] || matchingTech.WtT_Power_CI;
      return safeParseFloat(intensityField);
    }
  }
  
  return null;
}

/**
 * Apply T&D factor to a CI value based on country
 * @param {number|null} ci - Carbon intensity value
 * @param {boolean} applyFactor - Whether to apply the T&D factor
 * @param {string} country - Country for which to apply the factor
 * @param {Array} tdFactorsData - T&D factors data array
 * @returns {number|null} - Adjusted CI value
 */
export function applyTDFactor(ci, applyFactor = true, country = 'Default', tdFactorsData = []) {
  if (ci === null) {
    return null;
  }
  
  if (!applyFactor) {
    return ci;
  }
  
  // Get T&D factor for the country
  const tdFactor = getTDFactorForCountry(country, tdFactorsData);

  // If T&D factor found, apply it
  if (tdFactor !== null) {
    return ci / (1 - tdFactor);
  }

  
  // Constants for default T&D factors
  const US_DEFAULT_TD_FACTOR = 0.045; // 4.5% for US
  const OTHER_DEFAULT_TD_FACTOR = 0.143; // 14.3% for others
  const US_COUNTRY_NAME = 'United States';
  
  // Fallback to default T&D factor if not found
  const defaultTdFactor = country === US_COUNTRY_NAME ? US_DEFAULT_TD_FACTOR : OTHER_DEFAULT_TD_FACTOR;
  return ci / (1 - defaultTdFactor);
}

/**
 * Extract T&D factor for a specific country
 * @param {string} country - Country to get factor for
 * @param {Array} tdFactorsData - T&D factors data array
 * @returns {number|null} - T&D factor as decimal or null if not found
 */
export function getTDFactorForCountry(country, tdFactorsData) {
  if (!Array.isArray(tdFactorsData) || tdFactorsData.length === 0 || !country) {
    return null;
  }
  
  // Find the T&D factor for this country
  const factorEntry = tdFactorsData.find(entry => entry.Country === country);
  
  if (!factorEntry?.Percentage) {
    return null;
  }
  
  // Parse the percentage string (e.g., "4.5%") to a decimal value
  const percentageStr = factorEntry.Percentage.trim();
  const percentageRegex = /(\d+\.?\d*)%/;
  const percentageMatch = percentageStr.match(percentageRegex);
  
  // Fix: Access the capture group directly from the match array
  const percentageValue = percentageMatch?.[1];
  if (percentageValue) {
    const percentage = parseFloat(percentageValue);
    const PERCENTAGE_TO_DECIMAL = 100; // Divisor to convert percentage to decimal
    return percentage / PERCENTAGE_TO_DECIMAL;
  }
  
  return null;
}

/**
 * Extract unique countries from sales data
 * @param {Array} total3PGenSalesData - 3P gen sales data
 * @returns {Set} - Set of unique countries
 */
export function extractCountriesFromSalesData(total3PGenSalesData) {
  const countries = new Set();
  
  if (!Array.isArray(total3PGenSalesData)) {
    return countries;
  }
  
  total3PGenSalesData.forEach(item => {
    const region = item["Region | Grid"] || item.Region_Grid || '';
    if (region) {
      countries.add(extractCountry(region));
    }
  });
  
  return countries;
}

/**
 * Group processed items by country
 * @param {Array} processedItems - Processed certificate items
 * @returns {Object} - Items grouped by country
 */
export function groupItemsByCountry(processedItems) {
  const countryData = {};
  
  if (!Array.isArray(processedItems)) {
    return countryData;
  }
  
  processedItems
    .filter(item => item.country && item.volumeInKwh > 0 && item.ci !== null)
    .forEach(item => {
      if (!countryData[item.country]) {
        countryData[item.country] = [];
      }
      countryData[item.country].push(item);
    });
  
  return countryData;
}

/**
 * Calculate weighted average for array of items
 * @param {Array} items - Array of items with volumes and intensities
 * @returns {number} - Weighted average intensity
 */
export function calculateWeightedAverage(items) {
  if (!Array.isArray(items) || items.length === 0) {
    return 0;
  }
  
  let totalVolume = 0;
  let weightedSum = 0;
  
  items.forEach(item => {
    const volume = item.volumeInKwh || 0;
    const intensity = item.ci || 0;
    
    totalVolume += volume;
    weightedSum += volume * intensity;
  });
  
  if (totalVolume > 0) {
    return weightedSum / totalVolume;
  }
  
  return 0;
}

/**
 * Initialize intensity object with default values for all countries
 * @param {Set} countries - Set of country names
 * @param {string} intensityField - Name of the intensity field
 * @returns {Object} - Initialized intensity object
 */
export function initializeIntensityObject(countries, intensityField) {
  const intensities = {};
  
  countries.forEach(country => {
    intensities[country] = {
      [intensityField]: 0
    };
  });
  
  return intensities;
}

/**
 * Process renewable certificates data based on the selected year
 * Prorates R4Q values for current year based on completed quarters
 * @param {Array} renewableCertificatesData - The renewable certificates data array
 * @param {number} selectedYear - The selected year
 * @returns {Array} - The processed renewable certificates data
 */
export function processRenewableCertificates(renewableCertificatesData, selectedYear) {
  if (!renewableCertificatesData || !Array.isArray(renewableCertificatesData) || !selectedYear) {
    return renewableCertificatesData;
  }
  
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12
  
  // If selected year is not current year, return data as is
  if (selectedYear !== currentYear) {
    return renewableCertificatesData;
  }
  
  // Constants for months
  const JANUARY = 1;
  const MARCH = 3;
  const APRIL = 4;
  const JUNE = 6;
  const JULY = 7;
  const SEPTEMBER = 9;
  const OCTOBER = 10;
  const DECEMBER = 12;
  
  // Constants for quarters
  const NO_QUARTERS_COMPLETED = 0;
  const ONE_QUARTER_COMPLETED = 1;
  const TWO_QUARTERS_COMPLETED = 2;
  const THREE_QUARTERS_COMPLETED = 3;
  
  // Quarters in a year
  const QUARTERS_PER_YEAR = 4;
  
  // Determine completed quarters in the current year
  let completedQuarters = NO_QUARTERS_COMPLETED;
  if (currentMonth >= JANUARY && currentMonth <= MARCH) {
    completedQuarters = NO_QUARTERS_COMPLETED;
  } else if (currentMonth >= APRIL && currentMonth <= JUNE) {
    completedQuarters = ONE_QUARTER_COMPLETED;
  } else if (currentMonth >= JULY && currentMonth <= SEPTEMBER) {
    completedQuarters = TWO_QUARTERS_COMPLETED;
  } else if (currentMonth >= OCTOBER && currentMonth <= DECEMBER) {
    completedQuarters = THREE_QUARTERS_COMPLETED;
  }
  
  // If no quarters completed yet, return data as is
  if (completedQuarters === NO_QUARTERS_COMPLETED) {
    return renewableCertificatesData;
  }
  
  // Process each certificate record
  return renewableCertificatesData.map(record => {
    const processedRecord = { ...record };
    
    // Get the R4Q value
    let r4qValue = record.R4Q;
    
    // Convert to number if it's a string
    if (typeof r4qValue === 'string') {
      // Remove all non-numeric characters except decimal points and negative signs
      const nonNumericCharRegex = /[^\d.-]/g;
      r4qValue = parseFloat(r4qValue.replace(nonNumericCharRegex, '')) || 0;
    }
    
    // Apply the correct prorating formula: R4Q / 4 * completedQuarters
    const proratedValue = (r4qValue / QUARTERS_PER_YEAR) * completedQuarters;
    
    // Update the R4Q value
    processedRecord.R4Q = proratedValue;
    
    return processedRecord;
  });
}

/**
 * Process data for sheets with quarterly actuals based on selected year
 * @param {Object} data - The full data object with all sheets
 * @param {number} selectedYear - The selected year to filter actuals
 * @returns {Object} - The processed data with updated R4Q values or empty arrays for sheets with no data
 */
export function processDataByYear(data, selectedYear) {
  if (!data || !selectedYear) {
    return data;
  }
  
  const yearStr = selectedYear.toString();
  const sheetNames = ['Shell Gen Power', 'Total 3P Gen Sales', '3P Gen Tech Specific'];
  const result = { ...data };
  
  // Process each sheet that contains quarterly data
  sheetNames.forEach(sheetName => {
    result[sheetName] = processSheet(result[sheetName], yearStr);
  });
  
  // Process Renewable Certificates data
  const RENEWABLE_CERTIFICATES_SHEET = 'Renewable Certificates';
  if (result[RENEWABLE_CERTIFICATES_SHEET] && Array.isArray(result[RENEWABLE_CERTIFICATES_SHEET])) {
    result[RENEWABLE_CERTIFICATES_SHEET] = processRenewableCertificates(
      result[RENEWABLE_CERTIFICATES_SHEET], 
      selectedYear
    );
  }

  return result;
}

/**
 * Process individual sheet data
 * @param {Array} sheetData - Data from a specific sheet
 * @param {string} yearStr - Year string to filter data
 * @returns {Array} - Processed sheet data
 */
function processSheet(sheetData, yearStr) {
  // Handle invalid sheet data
  if (!sheetData || !Array.isArray(sheetData)) {
    return [];
  }
  
  // Check if any records have data for the selected year
  if (!hasDataForSelectedYear(sheetData, yearStr)) {
    return [];
  }
  
  // Process each item in the sheet
  return sheetData.map(item => processSheetItem(item, yearStr));
}

/**
 * Check if sheet has data for selected year
 * @param {Array} sheetData - Data from a specific sheet
 * @param {string} yearStr - Year string to filter data
 * @returns {boolean} - True if data exists for year
 */
function hasDataForSelectedYear(sheetData, yearStr) {
  return sheetData.some(item => {
    const keys = Object.keys(item);
    return keys.some(key => 
      key.includes(yearStr) && key.includes('Q') && key.includes('Actuals'));
  });
}

/**
 * Process an individual item from a sheet
 * @param {Object} item - Single item from sheet data
 * @param {string} yearStr - Year string to filter data
 * @returns {Object} - Processed item
 */
function processSheetItem(item, yearStr) {
  // Create a copy of the item to avoid modifying the original
  const processedItem = { ...item };
  
  // Find all quarterly actuals for the selected year
  const quarterFields = findQuarterlyFields(item, yearStr);
  
  // If we found quarterly fields, calculate and update the R4Q value
  if (quarterFields.length > 0) {
    updateR4QValue(processedItem, item, quarterFields);
  }
  
  return processedItem;
}

/**
 * Find quarterly fields for selected year
 * @param {Object} item - Data item
 * @param {string} yearStr - Year string to filter on
 * @returns {Array} - Array of field names
 */
function findQuarterlyFields(item, yearStr) {
  const allKeys = Object.keys(item);
  return allKeys.filter(key => 
    key.includes(yearStr) && key.includes('Q') && key.includes('Actuals')
  );
}

/**
 * Update R4Q value based on quarterly fields
 * @param {Object} processedItem - Item being processed 
 * @param {Object} originalItem - Original data item
 * @param {Array} quarterFields - Array of quarterly field names
 */
function updateR4QValue(processedItem, originalItem, quarterFields) {
  const yearTotal = calculateYearTotal(originalItem, quarterFields);
  
  // Update the R4Q field with the new calculated total if we have valid data
  if (yearTotal > 0) {
    processedItem.R4Q = yearTotal;
  }
}

/**
 * Calculate total from quarterly values
 * @param {Object} item - Data item
 * @param {Array} quarterFields - Array of quarterly field names
 * @returns {number} - Total value
 */
function calculateYearTotal(item, quarterFields) {
  let yearTotal = 0;
  
  quarterFields.forEach(field => {
    yearTotal += extractNumericValue(item[field]);
  });
  
  return yearTotal;
}

/**
 * Extract numeric value from mixed data types
 * @param {any} value - Value to extract number from
 * @returns {number} - Extracted number
 */
function extractNumericValue(value) {
  // Return 0 for undefined or null values
  if (value === undefined || value === null) {
    return 0;
  }
  
  // Handle numeric values directly
  if (typeof value === 'number') {
    return value;
  }
  
  // Handle string values
  if (typeof value === 'string') {
    return parseNumericString(value);
  }
  
  return 0;
}

/**
 * Parse numeric value from string
 * @param {string} valueStr - String to parse
 * @returns {number} - Parsed number
 */
function parseNumericString(valueStr) {
  // Handle strings that might contain numbers with spaces, commas, etc.
  // Remove all non-numeric characters except decimal points and negative signs
  const nonNumericCharRegex = /[^\d.-]/g;
  const cleanStr = valueStr.replace(nonNumericCharRegex, '').trim();
  
  // Handle empty or invalid strings
  if (!cleanStr || cleanStr === '-' || cleanStr === '.') {
    return 0;
  }
  
  return parseFloat(cleanStr) || 0;
}